import { useCallback, useEffect, useState } from "react";
import { PageHeader } from "../components/PageHeader";
import { AuthRequired } from "../components/AuthRequired";
import {
  ErrorBlock,
  LoadingBlock,
  MetricCard,
  MoneyValue,
  StatusChip,
} from "../components/CrmCards";
import { ActionTile, TileGrid } from "../ui/ActionTile";
import { Button, CardGrid, Disclosure, SectionLabel, Stack } from "../ui/primitives";
import { navItem } from "../components/navConfig";
import { mapAffiliateError } from "../api/affiliatePayoutsErrors";
import {
  getTreasurySummary,
  listPartnerPayables,
  type PartnerPayableRow,
  type TreasurySummaryResponse,
} from "../api/treasuryPayables";

// Treasury Ledger Payout v1 — read-only money dashboard, and the console's
// landing route (`/` and `*` both redirect here; `Layout.test.tsx` pins that).
//
// Consumes GET /v2/admin/bff/treasury/summary + GET
// /v2/admin/bff/partner-payables. Renders CRM cards only. Moves no
// funds, creates no proposals, and exposes no proposalId / invoiceId /
// escrow-bucket concept. Every money value comes pre-formatted from the
// BE; this page never does money math beyond counting rows by status.
//
// The screen has a second job, and under the action-first brief it is the
// FIRST one. This route used to be money-only, so an operator landing here
// every session had to read the sidebar to find anything else. It now opens
// with a grid of large action cards built from `navConfig` — the same array
// the sidebar renders — and the treasury position follows underneath.
//
// One count is wired to real data (partner payables needing action). The rest
// render without one, because this page fetches two endpoints and a tile may
// not invent a number it has not read. A tile with no count says nothing
// rather than "0".

interface DashboardData {
  summary: TreasurySummaryResponse;
  rows: PartnerPayableRow[];
}

function countByStatus(rows: PartnerPayableRow[], status: PartnerPayableRow["status"]): number {
  return rows.filter((r) => r.status === status).length;
}

/** The control-center cards, in the order the operator's brief lists them.
 *
 *  Each entry resolves against `navConfig`, so the label, icon, blurb and
 *  route all come from the one array the sidebar also renders — a card cannot
 *  drift from the nav, and a destination cannot exist in one and be missing
 *  from the other.
 *
 *  `Dashboard` and `Login` are deliberately absent: a tile pointing at the
 *  page you are already on is noise, and you cannot be looking at this screen
 *  without a session. */
const TILE_ROUTES: ReadonlyArray<string> = [
  "/control-plane",
  "/approvals",
  "/treasury",
  "/admin/partner-payables",
  "/escrow/refunds",
  "/settlement-control",
  "/devices",
  "/session",
  "/admin/affiliate/partners",
  "/admin/affiliate/redemptions",
];

function MoneyDashboardInner() {
  const [data, setData] = useState<DashboardData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setError(null);
    setLoading(true);
    try {
      // Two independent read-only GETs. Both must succeed to paint the
      // full dashboard; either rejection surfaces a single error block.
      const [summary, list] = await Promise.all([
        getTreasurySummary(),
        listPartnerPayables(),
      ]);
      setData({ summary, rows: list.items });
    } catch (e) {
      setError(mapAffiliateError(e).message);
      setData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  if (loading && data === null) return <LoadingBlock label="Loading treasury…" />;
  if (error) return <ErrorBlock message={error} onRetry={() => void load()} />;
  if (data === null) return <LoadingBlock label="Loading treasury…" />;

  const { summary, rows } = data;
  // "Available" = a payable BALANCE with no actionable request yet (BE reports
  // these as `ready` + nextAction `awaiting_request`). "Open requests" = an
  // actual payout_request awaiting admin action (requested/approved/
  // awaiting_tx/submitted). Splitting them keeps the dashboard honest.
  const OPEN_REQUEST = new Set(["requested", "approved", "awaiting_tx", "submitted"]);
  const availableCount = rows.filter(
    (r) => r.status === "ready" && r.nextAction === "awaiting_request",
  ).length;
  const openRequestCount = rows.filter((r) => OPEN_REQUEST.has(r.requestStatus ?? "")).length;
  const pendingCount = countByStatus(rows, "pending");
  const blockedCount = countByStatus(rows, "blocked");
  const paidCount = countByStatus(rows, "paid");

  const bal = summary.treasuryBalance;

  // Counts this page can honestly supply from the two endpoints it fetches.
  // A route absent from this map renders a tile with NO count rather than a
  // zero: "nothing needs you" and "we could not reach the server" must not
  // look alike on the screen used to decide whether to act.
  const TILE_COUNTS: Record<string, { count: number; label: string; urgent: boolean }> = {
    "/admin/partner-payables": {
      count: openRequestCount + blockedCount,
      label: "open",
      urgent: openRequestCount + blockedCount > 0,
    },
  };

  return (
    <Stack gap={26}>
      {/* Action cards come FIRST and take the most space. The operator's
          question on opening the console is "what can I do", not "what is the
          treasury balance" — the balance is one click away under Treasury,
          and still summarised below. */}
      <TileGrid>
        {TILE_ROUTES.map((route) => {
          const item = navItem(route);
          if (item === undefined || item.icon === undefined) return null;
          const Icon = item.icon;
          const c = TILE_COUNTS[route];
          return (
            <ActionTile
              key={route}
              to={route}
              label={item.label}
              description={item.blurb ?? ""}
              icon={<Icon size={22} />}
              count={c?.count}
              countLabel={c?.label}
              tone={c?.urgent ? "warn" : "neutral"}
            />
          );
        })}
      </TileGrid>

      <Stack gap={12}>
        <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
          <SectionLabel>Treasury position</SectionLabel>
          <span style={{ flex: 1 }} />
          <span className="g-num" style={{ fontSize: 12, color: "var(--fg-dim)" }}>
            chain {summary.chainId} · {summary.token}
          </span>
          <Button size="sm" onClick={() => void load()} disabled={loading}>
            {loading ? "Refreshing…" : "Refresh"}
          </Button>
        </div>

        <CardGrid min={252}>
          <MetricCard
            title="Treasury balance"
            accent="var(--ok)"
            badge={
              bal.available ? <StatusChip status="ready" /> : <StatusChip status="blocked" />
            }
            footer={
              summary.treasuryAddressShort ? (
                <span title={summary.treasuryAddress ?? undefined}>
                  wallet <code style={{ fontSize: 11 }}>{summary.treasuryAddressShort}</code>
                </span>
              ) : (
                "Treasury wallet not configured."
              )
            }
          >
            {bal.available ? (
              <MoneyValue display={bal.displayAmount} raw={bal.rawAmount} />
            ) : (
              <div style={{ fontSize: 14, color: "var(--fg-dim)" }}>
                On-chain balance unavailable
                <div style={{ fontSize: 11, marginTop: 4 }}>({bal.reason})</div>
              </div>
            )}
          </MetricCard>

          <MetricCard
            title="Partner payable reserve"
            accent="var(--info)"
            footer="Total off-chain liability owed to partners (available + open)."
          >
            <MoneyValue
              display={summary.partnerPayableReserve.displayAmount}
              raw={summary.partnerPayableReserve.rawAmount}
            />
          </MetricCard>

          <MetricCard
            title="Available partner payables"
            accent="var(--accent)"
            badge={<StatusChip status="available" />}
            footer={
              availableCount === 1
                ? "1 partner has a payable balance — awaiting a withdrawal request."
                : `${availableCount} partners have a payable balance — awaiting a withdrawal request.`
            }
            cta={{ label: "Open Partner Payables", to: "/admin/partner-payables" }}
          >
            <MoneyValue display={String(availableCount)} size={28} tone="var(--accent)" />
          </MetricCard>

          <MetricCard
            title="Open payout requests"
            accent="var(--warn)"
            footer={`${pendingCount} awaiting approval · ${blockedCount} blocked · ${paidCount} paid`}
            cta={{ label: "Review requests", to: "/admin/partner-payables?tab=pending" }}
          >
            <div style={{ display: "flex", gap: 14, alignItems: "baseline" }}>
              <MoneyValue display={String(openRequestCount)} size={28} tone="var(--warn)" />
              <span style={{ color: "var(--fg-dim)", fontSize: 12.5 }}>open requests</span>
            </div>
          </MetricCard>
        </CardGrid>
      </Stack>

      {/* The v1 model note. Real context for an operator new to the ledger
          model, and two paragraphs every returning operator has already read
          — which is exactly what a disclosure is for. */}
      <Disclosure summary="How partner payouts work in Treasury Ledger Payout v1">
        <div style={{ fontSize: 13, color: "var(--fg-muted)", paddingTop: 10 }}>
          <p>No third-party custody. No escrow bucket. No multisign proposal.</p>
          <p style={{ margin: 0 }}>
            Partner cash is an off-chain payable paid from the Gao treasury wallet. Approve a
            request, then record the treasury transfer txHash; the backend verifies it on-chain.
          </p>
        </div>
      </Disclosure>
    </Stack>
  );
}

export function MoneyDashboard() {
  return (
    <div>
      <PageHeader title="Dashboard" subtitle="What needs you, and where to go." />
      <AuthRequired>
        <MoneyDashboardInner />
      </AuthRequired>
    </div>
  );
}
